import {
	avatar,
	btn,
	Button,
	column,
	comp,
	Component,
	Config,
	cookies,
	DateTime,
	displaydatefield,
	displayfield, Format,
	h2,
	h3,
	h4,
	MaterialIcon,
	menu,
	p,
	router,
	Store,
	Table,
	table,
	tbar,
	Toolbar,
	win
} from "@intermesh/goui";
import {translate, t} from "./util/Translate.js";
import {currentUser, CurrentUser} from "./services/CurrentUser.js";
import {ServerApi} from "./util/ServerApi.js";
import {CurrentParticipant} from "./services/CurrentParticipant.js";
import {CurrentSection} from "./services/CurrentSection.js";
import {documentTypesStore} from "./data/DocumentTypeStore.js";
import {ParticipantDocument, ParticipantEntity} from "./data/ParticipantsStore.js";
import {customFieldsStore} from "./data/CustomFieldsStore.js";
import {EditDocumentDialog} from "./EditDocumentDialog.js";
import {CurrentCourse} from "./services/CurrentCourse.js";
import {serviceMessagesBtn} from "./components/ServiceMessages.js";
import {IfwFormat} from "./util/IfwFormat.js";
import {User} from "./model/User.js";
import {renderCustomDisplayField} from "./components/CustomField";
import {languagesStore} from "./data/LanguagesStore";

export class Profile extends Component {
	private documentsGrid: Table | undefined;
	private missingDocumentsGrid: Table | undefined;
	private missingDocumentsCard: Component | undefined;
	private smallScreen = false;

	constructor() {
		super("section");
		this.itemId = "profile";
		this.id = "profile";
		this.cls = "card";

		const mql = window.matchMedia("(max-width: 600px)");
		this.smallScreen = mql.matches;

		Promise.all([
			CurrentUser.readIf(),
			CurrentParticipant.readIf(),
			CurrentSection.readIf(),
			documentTypesStore.load()
		]).then(async (r: [User | undefined, ParticipantEntity, any, any]) => {
			if (CurrentParticipant.isInvited() && !CurrentParticipant.isRedirectedToInvitedCourse()) {
				await CurrentParticipant.goToInvitedCourse();
			}
			const user = r[0]!, participant = r[1];

			// If for some reason the session is lost (private browsing, SSO, API roundtrip), get the language ID from
			// the participant and reload the translation
			if (!sessionStorage.getItem("language")) {
				const participantLanguageId = participant.languageId || 1;
				const languages = await languagesStore.load();

				sessionStorage.setItem("languageId", String(participantLanguageId));
				for (const l of languages) {
					if (l.id == participantLanguageId) {
						sessionStorage.setItem("language", l.key.toUpperCase());
						await translate.load();
						break;
					}
				}
			}

			this.items.add(this.getTopBar(user, participant),
				this.getUserHeader(user, participant),
				this.getOtherShit(user, participant),
				this.getCoursesCard(participant),
				this.getParticipantInformationCard(participant),
				this.getBottomBar());

			// Only show documents if any are defined
			if (r[3].length > 0) {
				this.items.insert(4, this.getDocumentsCard(participant));
			}

			const pc = JSON.parse(localStorage.getItem("portalConfig")!);
			if (pc && pc.mayUseEva) {
				let ep = sessionStorage.getItem("evaPortal");
				if (ep) {
					CurrentUser.enableIdleLogout();
				}
			}

		});
		mql.addEventListener("change", (e) => {
			this.smallScreen = e.matches;
		});

	}

	private getTopBar(_user: User, participant: ParticipantEntity): Toolbar {
		return tbar({cls: "header"},
			"->",
			serviceMessagesBtn(),
			btn({
				cls: "ifw-top-btn",
				icon: "edit", title: t("Edit"), disabled: !participant.permissions!.write, handler: () => {
					router.goto("participant-edit");
				}
			}),
			btn({
				cls: "ifw-top-btn",
				icon: "exit_to_app", title: t("Logout"), handler: async () => {
					CurrentUser.logout();
				}
			}),
		);
	}

	private getUserHeader(user: User, participant: ParticipantEntity) {
		let ava = avatar({cls: "go-avatar", displayName: user.displayName});
		if (participant.photo) {
			ava.backgroundImage = ServerApi.thumbUrl(participant.photo, 70, 70);
		} else {
			ava.backgroundImage = "resources/male.png";
			ava.style.backgroundColor = "#ffffff";
		}
		return comp({
				cls: "hbox header gap", style: {
					alignItems: "center"
				}
			},
			ava,
			h3({text:participant.fullName})
		);
	}

	/**
	 * Render a component with variable content by use case
	 *
	 * @param _user
	 * @param participant
	 * @private
	 */
	private getOtherShit(_user: User, participant: ParticipantEntity): Component {
		const pc = JSON.parse(localStorage.getItem("portalConfig")!),
			currentSection = CurrentSection.get();

		let comps = [];
		if (pc.openCourseSelection && !participant.location) {
			const c = comp({cls: "row pad border-bottom"},
				comp({ flex: 1},
					h2({html: t("Welcome")}),

					p({
						html: t("Is the desired course not in the list?") + "&nbsp;" +
							t("Click on the button alongside to add a new course.")
					}),
				),

				btn({
					cls: "primary filled",
					handler: () => {
						router.goto("scorm-course-selection");
					},
					text: t("Select new course")
				})
			);
			comps.push(c);
		}
		if (participant.location) {
			const c = comp({cls: "row pad border-bottom"},
				comp({flex: 1},
					h2({
						cls: "ifw-title",
						html: t("Welcome at") + "&nbsp;<strong>" + currentSection.name + "</strong>"
					}),
					p({
						html: t("Press the button to start a new course"),
						flex: 1
					}),
				),
				btn({
					cls: "primary filled", text: t("Start new course"), handler: () => {
						this.startNewCourse()
					}
				}));
			comps.push(c);
		} else if (pc.mayRegister && !pc.openCourseSelection) {
			let pTxt = "";
			const c = comp({cls: "row pad border-bottom"},
				p({
					html: pTxt,
					flex: 1
				})
				, btn({
					cls: "primary filled", text: t("Start new course"), handler: () => {
						this.startNewCourse()
					}
				}));
			comps.push(c);
		}

		let invalidLicences = [];
		if (participant.licenses) {
			for (const license of participant.licenses) {
				if (!this.licenseIsValid(license)) {
					invalidLicences.push(license);
				}
			}

		}

		if (invalidLicences.length > 0) {
			let ilStore = new Store();
			ilStore.loadData(invalidLicences, false);
			const tbl = table({
				cls: "no-row-lines",
				store: ilStore,
				headers: false,
				fitParent: true,
				columns: [
					column({
						id: "succeeded",
						sortable: false,
						resizable: false,
						width: 50,
						hidden: this.smallScreen,
						renderer: (_value, record, _td, _table1, _storeIndex) => {
							let success = this.licenseIsValid(record);
							return btn({
								cls: (success ? "goui icon primary" : "goui icon"),
								icon: "redeem",
								title: record.course.name,
								handler: () => {
									return false;
								}
							})
						}
					}),

					column({
						id: "course.name",
						sortable: false,
						resizable: false,
						renderer: (_value, record, _td, _table1, _storeIndex) => {
							return comp({
								cls: "list-row"
							}, h3({text: record.course.name}));
						}
					}),
					column({
						id: "course.duration",
						sortable: false,
						resizable: false,
						hidden: this.smallScreen,
						width: 200,
						renderer: (_value, record, _td, _table1, _storeIndex) => {
							return comp({

								html: '<i class="icon small">timer</i>&nbsp;' + IfwFormat.ifwDuration(CurrentParticipant.getCourseDuration(record.course)),

							});
						}
					}),

					column({
						id: "course.id",
						sortable: false,
						resizable: false,
						width: this.smallScreen ? 50 : 120,
						align: "right",
						renderer: (_value, record, _td, _table1, _storeIndex) => {
							let cnf: Config<Button> = {
								cls: "primary filled",
								handler: async (_btn) => {
									await CurrentCourse.readIf(record.course.id);
									if (record.sectionId) {
										await CurrentSection.readIf(record.sectionId);
									}
									void router.goto("course-status");
								}
							};
							if (this.smallScreen) {
								cnf.icon = "play_arrow";
							} else {
								cnf.text = t("Start")
							}
							return btn(cnf)
						}
					})
				]
			});
			comps.push(comp({cls: "vbox gap pad border-bottom"}, h3({
				flex: 1,
				html: t("The following courses are available")
			}), tbl));
		}

		this.missingDocumentsCard = comp({cls: "vbox pad", hidden: true},
			h2({cls: "warning", html: '<i class="icon">warning</i> ' + t("Missing documents"), flex: 1})
		);
		this.renderMissingDocuments(participant);

		comps.push(this.missingDocumentsCard);

		return comp({}, ...comps);
	}

	private getCoursesCard(participant: ParticipantEntity): Component {
		let sectionBlocks = [];
		for (const section of participant.sections!) {
			const blk = comp({cls: "row"},
				h3({cls: "", flex: 1, html: section.name}),
				btn({
					icon: "print", cls: "fablike filled primary", handler: () => {
						window.open(ServerApi.url('elearning/frontend/certificates/' + section.id + '/' + participant.id, {}, true));
					}
				})
			);

			let sectionStore = new Store();
			sectionStore.loadData(section.licenses, false);
			const tbl = table({
				store: sectionStore,
				cls: "no-cta",
				headers: false,
				fitParent: true,
				columns: [
					column({
						id: "succeeded",
						sortable: false,
						resizable: false,
						width: 50,
						renderer: (_value, record, _td, _table1, _storeIndex) => {
							let success = this.licenseIsValid(record);
							return btn({
								cls: (success ? "icon success" : "icon danger"),
								icon: "layers",
								title: record.course.name,
								handler: () => {
									return false;
								}
							})
						}
					}),

					column({
						id: " course.name",
						sortable: false,
						resizable: false,
						renderer: (_value, record, _td, _table1, _storeIndex) => {
							let str = t("Not succeeded");
							if (record.expiresAt) {
								const dt = new DateTime(record.expiresAt);
								if (this.licenseIsValid(record)) {
									str = t("Expires at") + " " + dt.format("d-m-Y H:i");
								} else {
									str = t("Expired at") + " " + dt.format("d-m-Y H:i");
								}

							}
							return comp({
								cls: "row"
							}, h3({text: record.course.name}), h4({text: str}));
						}
					}),
				]
				,
				listeners: {
					rowclick: async ({target, storeIndex}) => {
						const cur = target.store.get(storeIndex)!;
						if (!cookies.get('sectionAuth')) {
							await CurrentSection.load(cur.sectionId);
						}
						await CurrentCourse.load(cur.courseId);
						void router.goto("course-status");
					}
				}
			});
			sectionBlocks.push(blk, tbl);

		}
		return comp({cls: "vbox gap pad border-bottom"},
			h2({cls: "ifw-title", html: t("Courses")}),
			...sectionBlocks
		);

	}

	private getDocumentsCard(participant: ParticipantEntity): Component {
		let documentBlocks = [];
		let documentsStore = new Store();
		documentsStore.loadData(participant.documents || []);
		const tbl = table({
			cls: "styled",
			store: documentsStore,
			fitParent: true,
			headers: false,
			emptyStateHtml: `<div class="ifw-empty-state"><div><i class="icon">attach_file</i></div><div>${t("No documents found")}</div></div>`,
			columns: [
				column({
					id: "attach",
					sortable: false,
					resizable: false,
					width: 50,
					renderer: (_value: any, record: ParticipantDocument) => {
						let cls = "icon", icon: MaterialIcon = "attach_file";
						const dt = new DateTime(record.expiryDate), now = new DateTime();
						if (now.compare(dt) > 0) {
							cls += " go-elearning-incident";
							icon = "warning";
						}

						return btn({
							cls: cls,
							icon: icon,
							title: record.documentType!.name,
							handler: () => {
								return false;
							}
						})
					}
				}),

				column({
					id: "documentType.name",
					sortable: false,
					resizable: false,
					htmlEncode: false,
					renderer: (_value: any, record: ParticipantDocument) => {

						let str = "";

						if (record.documentDate) {
							const dtd = new DateTime(record.documentDate);
							str += "<strong>" + t("Document date") + "</strong>: " + dtd.format("d F Y") + " ";
						}
						if (record.expiryDate) {
							const dte = new DateTime(record.expiryDate);
							str += "<strong>" + t("Document expiration date") + "</strong>: " + dte.format("d F Y") + " ";
						}

						if(str) {
							str = `<h5>${str}</h5>`
						}

						return `<h3>${Format.escapeHTML(record.documentType!.name)}</h3><h4>${Format.escapeHTML(record.identification)}</h4>${str}`;


					}
				}),
				column({
					id: "actions",
					width: 50,
					sortable: false,
					resizable: false,
					align: "right",
					renderer: (_value, record, _td, _table1, _storeIndex) => {
						const cntMenu = menu({}, btn({
								icon: "cloud_download", text: t("Download"), handler: (_b) => {
									window.open(ServerApi.url("download/" + record.blobId, {}, true), "_blank");
								}
							}),
							btn({
								icon: "delete", cls: "danger", text: t("Delete"), handler: (_b) => {
									const documentId = record.id, participantDocs = participant.documents;
									for (let i = 0, l = participantDocs!.length; i < l; i++) {
										const d = participantDocs![i];
										if (d.id == documentId) {
											d.markDeleted = true;
											break;
										}
									}
									CurrentParticipant.save({documents: participantDocs}).then(async (_response) => {
										sessionStorage.removeItem(CurrentParticipant.storageItem);
										const cp = await CurrentParticipant.readIf();
										this.documentsGrid!.store.loadData(cp.documents!, false);
										this.renderMissingDocuments(cp);
									});
								}
							}),
							btn({
								icon: "edit", text: t("Edit"), handler: (_b) => {
									this.editDocument(record.id);
								}
							}),
						);
						return btn({
							icon: "more_vert",
							cls: "",
							menu: cntMenu
						})
					}
				})
			]
		});
		documentBlocks.push(tbl);
		this.documentsGrid = tbl;
		return comp({cls: "vbox gap pad border-bottom"},
			comp({cls: "row"},
				h2({flex: 1, html: t("Documents")}),
				btn({
					icon: "add", cls: "fablike filled primary", handler: () => {
						this.editDocument();
					}
				})
			),
			...documentBlocks
		);
	}

	private getParticipantInformationCard(participant: ParticipantEntity): Component {
		const pc = JSON.parse(localStorage.getItem("portalConfig")!),
			showBirthDate = pc.showBirthDate;
		const pVbox = comp({cls: "flow", flex: 0.5, style: {minWidth: "350px"}},
			h2({cls: "ifw-title", html: t("Info")}),
			displayfield({icon: "mail", label: t("E-mail"), value: participant.email}),
			displaydatefield({
				icon: "cake",
				label: t("Date of birth"),
				value: participant.dateOfBirth,
				renderer: (v) => {
					if(v && showBirthDate) {
						return Format.date(v);
					}
					return "";
				}
			}),
			displayfield({icon: "domain", label: t("Company"), value: participant.company})
		);
		const pCFVbox = comp({cls: "flow", flex: 0.5, style: {minWidth: "350px"}});
		if (participant.customFields) {
			customFieldsStore.load().then((res) => {
				const fieldsets = res.filter(r => r.displayFrontend);
				for (const fs of fieldsets) {
					let items = [];
					const pcf = participant.customFields!;

					for (const f of fs.fields) {
						if (pcf.hasOwnProperty(f.databaseName) && pcf[f.databaseName] !== null) {
							const p = pcf[f.databaseName];
							if (typeof p === "boolean" || p.length > 0) {
								items.push(renderCustomDisplayField(f, p));
							}
						}
					}
					if (items.length > 0) {
						pCFVbox.items.add(h2({cls: "ifw-title", html: t(fs.name)}), ...items);
					}
				}
			});
		}
		return comp({cls: "vbox gap pad border-bottom"},
			comp({cls: "flow"}, pVbox, pCFVbox)
		);
	}

	private getBottomBar(): Toolbar {
		return tbar({cls: "ifw-center ifw-bbar"}, btn({
			icon: "shield",
			text: t("Privacy statement"),
			handler: async () => {
				const currentLang = localStorage.getItem("lang_"+sessionStorage.getItem("languageId") || "1");
				if (currentLang) {
					const l = JSON.parse(currentLang);
					win({
							title: t("Terms and conditions"),
							modal: true,
							resizable: true,
							maximizable: false,
							draggable: true,
							width: 800,
							height: 600
						}, comp({cls: "pad fit scroll"},
							comp({html: l.generalTerms}),
							comp({height: 60, text: ""}))
					)
						.show();
				}
			}
		}));
	}

	private licenseIsValid(record: { expiresAt: string | number | Date | undefined; }) {
		if (record.expiresAt) {
			const dt = new DateTime(record.expiresAt), now = new DateTime();
			return now.compare(dt) < 0;
		}
		return false;
	}

	private startNewCourse() {
		if (CurrentSection.get()) {
			router.goto("course-selection");
		} else {
			router.goto("section-selection");
		}
	}

	private editDocument(id?: string | undefined, documentTypeId?: string | undefined) {
		const dlg = new EditDocumentDialog();
		if (documentTypeId) {
			dlg.documentTypeFld.value = documentTypeId;
			dlg.fileInfoFld!.hidden = false;
		}
		dlg.on("close", async () => {
			const cp = await CurrentParticipant.readIf();
			this.documentsGrid!.store.loadData(cp.documents!, false);
			this.renderMissingDocuments(cp);
		});
		if (id) {
			dlg.load(id);
		}
		dlg.show();
	}

	private renderMissingDocuments(participant: ParticipantEntity): void {
		const mdComp = this.missingDocumentsCard!;
		mdComp.hidden = true;
		while (mdComp.items.count() > 1) {
			mdComp.items.removeAt(1);
		}
		if (participant.missingDocumentTypes && participant.missingDocumentTypes.length > 0) {
			for (const d of participant.missingDocumentTypes) {
				mdComp.items.add(comp({cls: "hbox"},
					comp({flex: 1, html: `${t("Participant needs to upload ")}: ${d.name}`}),
					btn({
						cls: "fablike filled primary small", icon: "add", handler: (_btn) => {
							this.editDocument(undefined, d.id);
						}
					})
				));
			}
			mdComp.hidden = participant.missingDocumentTypes.length === 0;
		}
	}
}